import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Bell, ChevronDown, Shield, Loader2, Target, ArrowUpRight } from "lucide-react";
import {
  useSentinelAlerts, useSentinelEscalations, useSentinelRootCauses,
  useUpdateAlert, useAckEscalation, useRunSentinelAgent,
} from "@/hooks/useSentinelCycle";
import { EscalationInbox } from "./EscalationInbox";
import { RootCauseTree } from "./RootCauseTree";
import { SEVERITY_CONFIG, STATUS_CONFIG, type Severity, type AlertStatus } from "./sentinel-constants";

interface Props {
  orgId?: string;
}

export default function SentinelAlerts({ orgId }: Props) {
  const [statusFilter, setStatusFilter] = useState("all");
  const { data: alerts = [], isLoading: loadingAlerts } = useSentinelAlerts(statusFilter);
  const { data: escalations = [] } = useSentinelEscalations();
  const { data: rootCauses = [] } = useSentinelRootCauses();
  const updateAlert = useUpdateAlert();
  const ackEscalation = useAckEscalation();
  const runAgent = useRunSentinelAgent();

  const [openAlerts, setOpenAlerts] = useState(true);
  const [openEsc, setOpenEsc] = useState(true);
  const [openRC, setOpenRC] = useState(false);

  const pendingEscalations = escalations.filter((e) => !e.ack_at);

  return (
    <div className="space-y-4">
      {/* ── Alerts ── */}
      <Collapsible open={openAlerts} onOpenChange={setOpenAlerts}>
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-2">
              <CollapsibleTrigger className="flex items-center gap-2 group flex-1 text-left">
                <ChevronDown className={`w-4 h-4 transition-transform ${openAlerts ? "" : "-rotate-90"}`} aria-hidden="true" />
                <Bell className="w-4 h-4 text-destructive" aria-hidden="true" />
                <CardTitle className="text-sm font-medium">Alertas</CardTitle>
                <Badge variant="outline" className="text-xs">{alerts.length}</Badge>
              </CollapsibleTrigger>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="open">Abertos</SelectItem>
                  <SelectItem value="acknowledged">Reconhecidos</SelectItem>
                  <SelectItem value="resolved">Resolvidos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CollapsibleContent>
            <CardContent className="space-y-2">
              {loadingAlerts ? (
                <><Skeleton className="h-16 w-full" /><Skeleton className="h-16 w-full" /></>
              ) : alerts.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">Nenhum alerta para os filtros atuais.</p>
              ) : (
                alerts.map((alert) => {
                  const sev = (alert.severity ?? "low") as Severity;
                  const status = (alert.status ?? "open") as AlertStatus;
                  const sevCfg = SEVERITY_CONFIG[sev] ?? SEVERITY_CONFIG.low;
                  const statusCfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.open;
                  const StatusIcon = statusCfg.icon;
                  return (
                    <div key={alert.id} className="flex items-start gap-3 p-3 rounded-md border border-border/60">
                      <StatusIcon className={`w-4 h-4 mt-0.5 shrink-0 ${statusCfg.color}`} aria-hidden="true" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-sm font-medium text-foreground truncate">{alert.title || "Alerta"}</span>
                          <Badge variant={sevCfg.badge} className="text-xs">{sevCfg.label}</Badge>
                          <Badge variant="outline" className="text-xs">{statusCfg.label}</Badge>
                        </div>
                        {alert.message && <p className="text-xs text-muted-foreground line-clamp-2">{alert.message}</p>}
                        <p className="text-[11px] text-muted-foreground mt-1">
                          {new Date(alert.created_at).toLocaleString("pt-BR")}
                        </p>
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        {status !== "resolved" && (
                          <>
                            {status === "open" && (
                              <Button size="sm" variant="outline" className="h-7 text-xs"
                                onClick={() => updateAlert.mutate({ id: alert.id, status: "acknowledged" })}>
                                Reconhecer
                              </Button>
                            )}
                            <Button size="sm" variant="outline" className="h-7 text-xs"
                              onClick={() => updateAlert.mutate({ id: alert.id, status: "resolved" })}>
                              Resolver
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs"
                              disabled={runAgent.isPending}
                              onClick={() => runAgent.mutate({ agentId: "root-cause", org_id: orgId, alertId: alert.id })}>
                              {runAgent.isPending && runAgent.variables?.alertId === alert.id
                                ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                : <Target className="w-3 h-3 mr-1" aria-hidden="true" />}
                              Causa raiz
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* ── Escalations ── */}
      <Collapsible open={openEsc} onOpenChange={setOpenEsc}>
        <Card>
          <CardHeader className="pb-2">
            <CollapsibleTrigger className="flex items-center gap-2 w-full text-left">
              <ChevronDown className={`w-4 h-4 transition-transform ${openEsc ? "" : "-rotate-90"}`} aria-hidden="true" />
              <ArrowUpRight className="w-4 h-4 text-orange-500" aria-hidden="true" />
              <CardTitle className="text-sm font-medium">Escalações</CardTitle>
              <Badge variant={pendingEscalations.length > 0 ? "destructive" : "outline"} className="text-xs">
                {pendingEscalations.length} pendente{pendingEscalations.length === 1 ? "" : "s"}
              </Badge>
            </CollapsibleTrigger>
          </CardHeader>
          <CollapsibleContent>
            <CardContent>
              <EscalationInbox escalations={escalations} onAck={(id) => ackEscalation.mutate(id)} />
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* ── Root Causes ── */}
      <Collapsible open={openRC} onOpenChange={setOpenRC}>
        <Card>
          <CardHeader className="pb-2">
            <CollapsibleTrigger className="flex items-center gap-2 w-full text-left">
              <ChevronDown className={`w-4 h-4 transition-transform ${openRC ? "" : "-rotate-90"}`} aria-hidden="true" />
              <Shield className="w-4 h-4 text-indigo-500" aria-hidden="true" />
              <CardTitle className="text-sm font-medium">Análises de Causa Raiz</CardTitle>
              <Badge variant="outline" className="text-xs">{rootCauses.length}</Badge>
            </CollapsibleTrigger>
          </CardHeader>
          <CollapsibleContent>
            <CardContent>
              <RootCauseTree rootCauses={rootCauses} />
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  );
}
